import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
// import {MessagePackHubProtocol} from "@microsoft/signalr-protocol-msgpack"

import { ContextInfo } from './ContextInfo';

import configStore from '../../stores/ConfigStore';
import contextStore from '../../stores/ContextStore';
import notificationStore from '../../stores/NotificationStore';

import { ParticipantInfo } from 'meet-addons-sdk';
import { runInAction } from 'mobx';
import meDataService from '../auth/MeDataService';
import { MeetingStateChangedInfo } from './MeetingStateChangedInfo';

import * as Sentry from '@sentry/react';
import i18n from '../../services/localization/i18n';
import { ClientTelemetryEvent } from '../tx/ClientTelemetryEvent';
import { AchivementsRequest } from '../api/AchievementsRequest';
import { PredefinedAchievementScenario } from '../api/PredefinedAchievementScenario';
import { PredefinedAchievementCheckpoint } from '../api/PredefinedAchievementCheckpoint';
import achievementsService from '../api/AchievementsService';
import telemetryDataService from '../tx/TelemetryDataService';
import { isTeamsAddonMode } from '../../utils';
import tokenRefreshingService from '../auth/TokenRefreshingService';
import { NOTIFICIATION_TOAST_DELAY } from '../../app/config';

class Signaling {
    public contextInfo!: ContextInfo;
    public connection!: HubConnection;
    private initTask?: Promise<void>;

    public refreshing: boolean = false;

    private correlationId!: string;
    private disconnectTime?: number;

    private notificationHandlers: number[] = [];
    private toastShown: boolean = false;

    public onContextSync: (e: ContextInfo) => void =
        // tslint:disable-next-line: no-console
        (e: ContextInfo) =>
            console.warn('[Spa][Signaling]:onContextSync-NOP', e);

    public onEndMeetSuggestion: () => void =
        // tslint:disable-next-line: no-console
        () => console.warn('[Spa][Signaling]:onEndMeetSuggestion-NOP');

    public initAsync(forced: boolean = false): Promise<void> {
        if (this.initTask && !forced) {
            return this.initTask;
        }

        this.refreshing = false;

        this.correlationId = Date.now().toString();
        const url = `${configStore.hosts.web}/hubs/shellSync`;

        this.connection = new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: async () =>
                    await configStore.getTokenAsync(),
            })
            .withAutomaticReconnect()
            // .withHubProtocol(new MessagePackHubProtocol())
            .configureLogging(LogLevel.Error)
            .build();

        this.connection.onclose((error) => {
            contextStore.setOnline(false);

            if (this.refreshing) {
                return;
            }

            this.disconnectTime = performance.now();

            let withError = !!error;
            let errorId: string = '';
            let silent = true;

            if (withError) {
                // tslint:disable-next-line: no-console
                console.error(error);

                errorId = Sentry.captureException(error);
                const errorMessage = error != null ? error.message : '';
                const message = i18n
                    .t('toast_disconnected_message')
                    .replace('{state}', this.connection.state)
                    .replace('{error}', `"${errorMessage}"`);

                this.notificationHandlers.push(
                    notificationStore.delayToast(
                        message,
                        'error',
                        NOTIFICIATION_TOAST_DELAY,
                        null,
                        () => {
                            this.toastShown = true;
                        }
                    )
                );
                silent = false;
            }

            const txEvent = {
                correlationId: this.correlationId,
                environment: configStore.environment,
                exceptionMessage: JSON.stringify(error),
                name: 'spa_meetApp_disconnect',
                sentryEventId: errorId,
                silent,
                source: 'meetApp',
                success: !error,
                timeToComplete: 0,
                version: configStore.version,
            } as ClientTelemetryEvent;

            telemetryDataService.reportAsync(txEvent);
        });

        this.connection.onreconnecting((error) => {
            contextStore.setOnline(false);

            if (this.refreshing) {
                return;
            }

            let sentryErrorId = '';
            let silent = true;
            let withError = !!error;

            if (withError) {
                sentryErrorId = Sentry.captureException(error);

                const errorMessage = error != null ? error.message : '';

                const message = i18n
                    .t('toast_reconnecting_message')
                    .replace('{state}', this.connection.state)
                    .replace('{error}', `"${errorMessage}"`);

                this.notificationHandlers.push(
                    notificationStore.delayToast(
                        message,
                        'warning',
                        NOTIFICIATION_TOAST_DELAY,
                        null,
                        () => {
                            this.toastShown = true;
                        }
                    )
                );
                silent = false;
            }

            let duration = 0;
            if (this.disconnectTime) {
                duration = performance.now() - this.disconnectTime;
            }

            const txEvent = {
                correlationId: this.correlationId,
                environment: configStore.environment,
                exceptionMessage: JSON.stringify(error),
                name: 'spa_meetApp_reconnecting',
                sentryEventId: sentryErrorId,
                silent,
                source: 'meetApp',
                success: !withError,
                timeToComplete: duration,
                version: configStore.version,
            } as ClientTelemetryEvent;
            telemetryDataService.reportAsync(txEvent);
        });

        this.connection.onreconnected(() => {
            this.refreshing = false;

            contextStore.setOnline(true);

            if (this.toastShown) {
                const message = i18n
                    .t('toast_reconnected_message')
                    .replace('{state}', this.connection.state);

                notificationStore.showToast(message, 'success', 3000);
            }

            this.toastShown = false;
            this.notificationHandlers.forEach((handler) =>
                notificationStore.cancel(handler)
            );
            this.notificationHandlers = [];

            let duration = 0;
            if (this.disconnectTime) {
                duration = performance.now() - this.disconnectTime;
            }

            const txEvent = {
                correlationId: this.correlationId,
                environment: configStore.environment,
                name: 'spa_meetApp_reconnected',
                silent: false,
                source: 'meetApp',
                success: true,
                timeToComplete: duration,
                version: configStore.version,
            } as ClientTelemetryEvent;
            telemetryDataService.reportAsync(txEvent);
        });

        this.initTask = this.startConnection();
        this.initTask.then((_) => {
            contextStore.setOnline(true);
        });
        return this.initTask;
    }

    public disconnect = (refreshing: boolean = false) => {
        this.refreshing = refreshing;

        if (
            this.connection &&
            this.connection.state === HubConnectionState.Connected
        ) {
            this.connection.stop();
        }
    };

    public notifyProfileChange = () => {
        this.initAsync().then(() => {
            this.connection.invoke('ProfileChange');
        });
    };

    private startConnection = () => {
        if (this.connection.state !== HubConnectionState.Disconnected) {
        }

        return this.connection
            .start()
            .then(() => {
                this.connection.on('Ping', (e) => this.onPing(e));
                this.connection.on('ContextSync', (e) => this.onContextSync(e));
                this.connection.on('ParticipantJoin', (e) =>
                    this.onParticipantJoin(e)
                );
                this.connection.on('ParticipantLeave', (e) =>
                    this.onParticipantLeave(e)
                );
                this.connection.on('MeetingStateChanged', (e) =>
                    this.onMeetingStateChanged(e)
                );
                this.connection.on('MyProfileChanged', () =>
                    this.onMyProfileChanged()
                );
                this.connection.on('EndMeetSuggestion', () =>
                    this.onEndMeetSuggestion()
                );

                this.connection.invoke('Sync');

                this.refreshing = false;
            })
            .catch((e) => {
                // tslint:disable-next-line: no-console
                console.error(
                    '[MeetApp]::signaling::startConnectionAsync-ERROR',
                    e
                );

                if (e && e.statusCode === 401) {
                    tokenRefreshingService.refreshAsync().then(async () => {
                        this.refreshing = true;
                        await this.initAsync(true);
                    });
                } else {
                    // note: reload page to get fresh token
                    if (!sessionStorage.getItem('meet-app-reload')) {
                        sessionStorage.setItem(
                            'meet-app-reload',
                            Date.now.toString()
                        );
                        window.location.reload();
                    } else {
                        // TODO: m1, 15.2.2020 - Report this to tx
                        const cid = Sentry.captureException(e);

                        notificationStore.showToast(
                            `General signaling fault - ${cid}. Please reload the page.`,
                            'error'
                        );
                    }
                }
            });
    };

    private onPing = (participantJoinInfos: ParticipantInfo[]) => {
        contextStore.addParticipants(participantJoinInfos);
    };

    private onParticipantJoin = (
        participantJoinInfos: HubParticipantInfo[]
    ) => {
        if (contextStore.addParticipants(participantJoinInfos)) {
            notificationStore.showToast(
                i18n
                    .t('participant_join_message')
                    .replace(
                        '{participant}',
                        participantJoinInfos[0].displayName || 'Guest'
                    ),
                'success'
            );

            const { resourceId, resourceType } = contextStore!;
            const { version, environment } = configStore!;

            const scenario = isTeamsAddonMode
                ? PredefinedAchievementScenario.TEAMS_ONBOARDING
                : PredefinedAchievementScenario.MEET_GENERAL;

            const achievement: AchivementsRequest = {
                scenario: scenario,
                checkpoint:
                    PredefinedAchievementCheckpoint.MEET_PARTICIPANT_JOINED,
                resourceId: resourceId!,
                resourceType: resourceType!,
            };

            achievementsService.postAsync(achievement);

            const txEvent = {
                correlationId: 'N/A',
                environment: environment,
                name: 'spa_meetApp_participantJoined',
                sentryEventId: 'N/A',
                silent: false,
                source: 'meetApp',
                success: true,
                timeToComplete: 0,
                version: version,
                properties: {
                    resourceId: resourceId,
                },
            } as ClientTelemetryEvent;

            telemetryDataService.reportAsync(txEvent);
        }

        participantJoinInfos.forEach((p) => {
            if (
                p &&
                p.addonIdentifier === 'roster' &&
                p.userIdentifier &&
                p.userIdentifier !== this.contextInfo.userIdentifier
            ) {
                // there is new participant - lets tell him we are online
                this.reportAsync(p.userIdentifier);
            }
        });
    };

    private onParticipantLeave = (leaveInfos: ParticipantInfo[]) => {
        contextStore.removeParticipants(leaveInfos);
        notificationStore.showToast(
            i18n
                .t('participant_left_message')
                .replace('{participant}', leaveInfos[0].displayName!),
            'warning'
        );
    };

    private reportAsync(userIdentifier: string) {
        return this.initAsync().then(() => {
            this.connection.invoke('Report', userIdentifier);
        });
    }

    public onMeetingStateChanged = (changeInfo: MeetingStateChangedInfo) => {
        contextStore.setMeetingStateChangeInfo(changeInfo);
    };

    private async onMyProfileChanged() {
        const meData = await meDataService.getMeAsync();
        runInAction(() => configStore.setMe(meData));
    }
}

class HubParticipantInfo extends ParticipantInfo {
    public userIdentifier!: string;
}

export default new Signaling();
